import { useState, useRef, useCallback, useEffect } from 'react';
import { WebSocketClient, createWebSocketClient } from '@/lib/websocket';
import { 
  ConnectionStatus, 
  AudioDataMessage, 
  TranscriptionMessage, 
  ErrorMessage,
  ParticipantUpdateMessage,
  StatusMessage
} from '@speech-translator/shared';

export interface SpeakerWebSocketState {
  connectionStatus: ConnectionStatus;
  participantCount: number;
  error: string | null;
  isConnected: boolean;
}

export interface SpeakerWebSocketCallbacks {
  onTranscription?: (message: TranscriptionMessage) => void;
  onError?: (message: ErrorMessage) => void;
  onStatusChange?: (status: ConnectionStatus) => void;
  onParticipantUpdate?: (message: ParticipantUpdateMessage) => void;
}

export function useSpeakerWebSocket(callbacks: SpeakerWebSocketCallbacks = {}) {
  const [state, setState] = useState<SpeakerWebSocketState>({
    connectionStatus: 'disconnected',
    participantCount: 0,
    error: null,
    isConnected: false
  });

  const wsClientRef = useRef<WebSocketClient | null>(null);
  const currentRoomIdRef = useRef<string | null>(null);

  // Update connection status
  const updateConnectionStatus = useCallback((status: ConnectionStatus) => {
    setState(prev => ({ 
      ...prev, 
      connectionStatus: status,
      isConnected: status === 'connected'
    }));
    callbacks.onStatusChange?.(status);
  }, [callbacks]);

  // Handle transcription messages
  const handleTranscription = useCallback((message: TranscriptionMessage) => {
    callbacks.onTranscription?.(message);
  }, [callbacks]);

  // Handle error messages
  const handleError = useCallback((message: ErrorMessage) => {
    setState(prev => ({ ...prev, error: message.data.message }));
    callbacks.onError?.(message);
  }, [callbacks]);

  // Handle status messages
  const handleStatus = useCallback((message: StatusMessage) => {
    setState(prev => ({ 
      ...prev, 
      participantCount: message.data.participantCount || 0 
    }));
  }, []);

  // Handle participant updates
  const handleParticipantUpdate = useCallback((message: ParticipantUpdateMessage) => {
    setState(prev => ({ 
      ...prev, 
      participantCount: message.data.participantCount 
    }));
    callbacks.onParticipantUpdate?.(message);
  }, [callbacks]);

  // Connect to a room
  const connect = useCallback(async (roomId: string): Promise<void> => {
    try {
      // Disconnect existing connection
      if (wsClientRef.current) {
        wsClientRef.current.disconnect();
      }

      // Clear any previous errors
      setState(prev => ({ ...prev, error: null }));

      // Create new WebSocket client
      const client = createWebSocketClient(roomId, 'speaker');
      wsClientRef.current = client;
      currentRoomIdRef.current = roomId;

      // Set up message handlers
      client.onMessage('transcription', handleTranscription);
      client.onMessage('error', handleError);
      client.onMessage('status', handleStatus);
      client.onMessage('participant_update', handleParticipantUpdate);

      // Set up status change handler
      client.onStatusChange(updateConnectionStatus);

      // Connect to WebSocket
      await client.connect();

      // Send join room message
      client.send({
        type: 'join_room',
        timestamp: Date.now(),
        data: {
          roomId,
          role: 'speaker'
        }
      });

    } catch (error) {
      console.error('Failed to connect to room:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to connect to room';
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    }
  }, [handleTranscription, handleError, handleStatus, handleParticipantUpdate, updateConnectionStatus]);

  // Disconnect from room
  const disconnect = useCallback(() => {
    if (wsClientRef.current) {
      wsClientRef.current.disconnect();
      wsClientRef.current = null;
    }
    currentRoomIdRef.current = null;
    setState(prev => ({ 
      ...prev, 
      connectionStatus: 'disconnected',
      isConnected: false,
      participantCount: 0,
      error: null
    }));
  }, []);

  // Send audio data
  const sendAudioData = useCallback((audioData: string, sequence: number) => {
    if (!wsClientRef.current?.isConnected()) {
      console.warn('WebSocket not connected, cannot send audio data');
      return;
    }

    const message: AudioDataMessage = {
      type: 'audio_data',
      timestamp: Date.now(),
      data: {
        audioData,
        sequence
      }
    };

    wsClientRef.current.send(message);
  }, []);

  // Reconnect to current room
  const reconnect = useCallback(async () => {
    if (currentRoomIdRef.current) {
      await connect(currentRoomIdRef.current);
    }
  }, [connect]);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // Get current room ID
  const getCurrentRoomId = useCallback(() => {
    return currentRoomIdRef.current;
  }, []);

  // Check if ready to send audio
  const isReadyForAudio = useCallback(() => {
    return state.isConnected && wsClientRef.current?.isConnected();
  }, [state.isConnected]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    // State
    ...state,
    
    // Actions
    connect,
    disconnect,
    reconnect,
    sendAudioData,
    clearError,
    
    // Getters
    getCurrentRoomId,
    isReadyForAudio
  };
}